/**
 * Video che si riproduce con lo scroll e fa rewind sui pin hover
 */

// Funzione per rilevare il browser
const PIN_STATE_ATTR = 'data-pin-state';
const PIN_STATE_ACTIVE = 'active';
const PIN_STATE_VISIBLE = 'visible';
const PIN_STATE_HIDDEN = 'hidden';
const PIN_HIDE_DELAY_MS = 250;
const PIN_SHOW_STAGGER_MS = 80;
const DEFAULT_OVERLAY_COLOR = '#e4dcd3';
const setPinStateAttr = (pin, state) => {
  if (!pin) return;
  pin.setAttribute(PIN_STATE_ATTR, state);
};
const getPinStateAttr = (pin) => pin?.getAttribute(PIN_STATE_ATTR);

// Ultima posizione nota del puntatore (coordinate viewport).
// Chrome desktop spara mouseleave/mouseenter anche a mouse fermo quando cambia il layout sotto
// il cursore (es. la freccia dell'overlay che compare sopra il pin): prima di chiudere l'hover
// verifichiamo dove si trova davvero il puntatore.
let lastPointerPosition = null;
if (typeof document !== 'undefined') {
  document.addEventListener('mousemove', (e) => {
    lastPointerPosition = { x: e.clientX, y: e.clientY };
  }, { passive: true });
  document.addEventListener('mouseout', (e) => {
    if (!e.relatedTarget) lastPointerPosition = null; // Il mouse è uscito dalla finestra
  }, { passive: true });
}

const isPointerInside = (el) => {
  if (!el || !lastPointerPosition) return false;
  const rect = el.getBoundingClientRect();
  if (!rect.width || !rect.height) return false;
  const { x, y } = lastPointerPosition;
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
};

function detectBrowser() {
  const ua = navigator.userAgent;
  const isChrome = /Chrome/.test(ua) && /Google Inc/.test(navigator.vendor);
  const isSafari = /Safari/.test(ua) && /Apple Computer/.test(navigator.vendor);
  
  return {
    isChrome,
    isSafari,
    isOther: !isChrome && !isSafari
  };
}

function isCantinaPath(pathname) {
  if (!pathname) return false;
  const hasCantinaSegment = pathname.includes('/cantine/') || pathname.includes('/wineries/');
  if (!hasCantinaSegment) return false;
  return !(/\/cantine\/?$/.test(pathname) || /\/wineries\/?$/.test(pathname));
}

function isCantinaArchivePath(pathname) {
  if (!pathname) return false;
  return /\/cantine\/?$/.test(pathname) || /\/wineries\/?$/.test(pathname);
}

function findCantinaData(url, fallbackLogo = null) {
  let logo = fallbackLogo || null;
  let bg = DEFAULT_OVERLAY_COLOR;
  let bgImage = null;

  if (typeof window !== 'undefined' && Array.isArray(window.cantineData)) {
    const normalizedHref = url.href.replace(/\/+$/, '');
    const targetCantina = window.cantineData.find((cantina) => {
      if (!cantina) return false;

      const candidateUrls = [cantina.url, cantina.permalink]
        .filter(Boolean)
        .map((candidate) => String(candidate).replace(/\/+$/, ''));

      const slug = cantina.slug ? String(cantina.slug).replace(/^\/|\/$/g, '') : '';

      const matchesUrl = candidateUrls.some((candidate) => candidate && normalizedHref.includes(candidate));
      const matchesSlug = slug && normalizedHref.includes(`/${slug}`);

      return matchesUrl || matchesSlug;
    });

    if (targetCantina) {
      logo = targetCantina.logo || logo;
      bg = targetCantina.colore || bg;
      bgImage = targetCantina.hero_bg || bgImage;
    }
  }

  return {
    logo: logo || null,
    bg: bg || DEFAULT_OVERLAY_COLOR,
    bgImage: bgImage || null
  };
}

function prepareCantinaTransition(href, pinData = null) {
  if (typeof window === 'undefined') return;

  let url;
  try {
    url = new URL(href, window.location.origin);
  } catch (err) {
    console.warn('SimpleScrollVideo: invalid cantina URL', href, err);
    return;
  }

  if (url.origin !== window.location.origin) return;

  const pathname = url.pathname.endsWith('/') ? url.pathname : `${url.pathname}/`;
  const isCantinaLink = typeof window.isCantinaSingola === 'function'
    ? window.isCantinaSingola(pathname)
    : isCantinaPath(pathname);
  const isCantinaArchive = typeof window.isCantinaArchive === 'function'
    ? window.isCantinaArchive(pathname)
    : isCantinaArchivePath(pathname);

  if (!isCantinaLink) {
    if (isCantinaArchive) {
      const params = {
        logo: null,
        bg: DEFAULT_OVERLAY_COLOR,
        bgImage: null,
        hideLogo: true
      };
      if (typeof window.setCustomOverlayParams === 'function') {
        window.setCustomOverlayParams(params);
      } else {
        window.customOverlayParams = params;
      }
    } else if (typeof window.setCustomOverlayParams === 'function') {
      window.setCustomOverlayParams(null);
    } else {
      window.customOverlayParams = null;
    }
    return;
  }

  const fallbackLogo = pinData?.hover?.logo_cantina || pinData?.logo || null;
  const overlayData = findCantinaData(url, fallbackLogo);
  const overlayLogo = overlayData.logo || pinData?.hover?.logo_cantina || null;
  const shouldHideLogo = !overlayLogo;

  const params = {
    logo: overlayLogo,
    bg: overlayData.bg || DEFAULT_OVERLAY_COLOR,
    bgImage: overlayData.bgImage,
    hideLogo: shouldHideLogo
  };

  if (typeof window.setCustomOverlayParams === 'function') {
    window.setCustomOverlayParams(params);
  } else {
    window.customOverlayParams = params;
  }
}

function updateNavMetaForLink(href) {
  if (typeof window === 'undefined' || typeof window.updateNavMetaFromAnchor !== 'function') return;
  const anchor = document.createElement('a');
  anchor.href = href;
  window.updateNavMetaFromAnchor(anchor);
}

class SimpleScrollVideo {
    constructor(videoElement, config = {}) {
      this.video = videoElement;
      this.browser = detectBrowser();
      
      // Impostazioni ottimizzate per browser
      const browserSettings = this.browser.isSafari ? {
        rewindSpeed: 0.015, // Safari richiede valori più bassi per smoothness
        smoothness: 0.25, // Movimento scroll più fluido su Safari
        returnSmoothness: 0.030, // Ritorno più fluido su Safari
        firstPlaybackSmoothness: 0.02, // Prima animazione più lenta su Safari
      } : {
        // Chrome e altri browser
        rewindSpeed: 0.015,
        smoothness: 0.2,
        returnSmoothness: 0.035,
        firstPlaybackSmoothness: 0.019 // Prima animazione più lenta su Chrome
      };
      
      // Filtra i parametri config per rimuovere valori vuoti o non validi
      const validConfig = {};
      for (const key in config) {
        const value = config[key];
        // Mantieni solo valori validi per i parametri che vogliamo controllare da browserSettings
        const browserControlledParams = ['rewindSpeed', 'smoothness', 'returnSmoothness', 'firstPlaybackSmoothness'];
        
        // Se è un parametro controllato dal browser, ignoralo se vuoto o non valido
        if (browserControlledParams.includes(key)) {
          if (value === '' || value === null || value === undefined) {
            continue; // Salta questo parametro, userà browserSettings
          }
          // Se è una stringa vuota, salta
          if (typeof value === 'string' && value.trim() === '') {
            continue; // Salta questo parametro, userà browserSettings
          }
        }
        
        // Per tutti gli altri parametri, mantieni i valori validi
        if (value !== '' && value !== null && value !== undefined) {
          if (typeof value === 'number' && !isNaN(value)) {
            validConfig[key] = value;
          } else if (typeof value === 'boolean') {
            validConfig[key] = value;
          } else if (typeof value === 'string' && value.trim() !== '') {
            // Prova a convertire stringhe numeriche
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              validConfig[key] = numValue;
            } else {
              validConfig[key] = value;
            }
          }
        }
      }
      
      const DEFAULT_SCROLL_TRIGGER = 500;
      const MAX_LAPTOP_WIDTH = 1920;
      const responsiveScrollTrigger =
        typeof window !== 'undefined' && window.innerWidth > MAX_LAPTOP_WIDTH
          ? DEFAULT_SCROLL_TRIGGER * 2
          : DEFAULT_SCROLL_TRIGGER;

      this.config = {
        scrollTrigger: responsiveScrollTrigger, // px di scroll per avviare il video
        videoDuration: 1.76, // durata video in secondi
        fps: 30,
        debug: false,
        rewindToFrame: 3, // Invece di tornare a 0, torna a frame 1 (un frame dopo l'inizio)
        lockAfterFirstPlay: true, // Dopo la prima riproduzione, il video rimane alla fine
        ...browserSettings,  // Browser settings hanno priorità sui default
        ...validConfig       // Config utente valido ha priorità su tutto
      };
      
      // Debug per verificare che i browserSettings vengano applicati
      // if (this.config.debug) {
      //   console.log('🔧 Browser Settings Applicati:', {
      //     browser: this.browser.isSafari ? 'Safari' : 'Chrome',
      //     rewindSpeed: this.config.rewindSpeed,
      //     smoothness: this.config.smoothness,
      //     returnSmoothness: this.config.returnSmoothness,
      //     firstPlaybackSmoothness: this.config.firstPlaybackSmoothness,
      //     validConfigKeys: Object.keys(validConfig),
      //     validConfigValues: validConfig
      //   });
      // }
      
      this.state = {
        isVideoPlaying: false,
        isScrollActive: false,
        isHovering: false,
        currentTargetTime: 0,
        lastScrollY: 0,
        animationFrame: null,
        rewindInterval: null,
        hasReachedEnd: false, // Flag per tracciare se il video ha raggiunto la fine almeno una volta
        pinsVisible: false // Flag per tracciare se i pin sono visibili
      };
      
      this.pins = [];
      this.pinsContainer = null; // Riferimento al container dei pin
      this.init();
    }
    
    // Metodo per attivare il video su mobile
    // Su Safari mobile, un video paused non mostra i frame anche se cambi currentTime
    // Soluzione: fai play() ma con playbackRate = 0 per "congelarlo"
    activateVideoForMobile() {
      if (!this.video) return;
      
      // Imposta playbackRate a 0 per congelare il video
      // Il video sarà tecnicamente "playing" ma non avanza da solo
      this.video.playbackRate = 0;
      
      // Fai play() per attivare il rendering dei frame
      this.video.play()
        // .then(() => {
        //   console.log('✅ Video activated for mobile (playbackRate = 0)');
        // })
        .catch(err => {
          console.warn('⚠️ Video play failed:', err.name, '- trying without user interaction requirement');
          // Fallback: prova senza promise (alcuni browser vecchi)
          try {
            this.video.play();
          } catch (e) {
            console.warn('⚠️ Video play fallback also failed');
          }
        });
    }
    
    init() {
      if (!this.video) {
        console.warn('SimpleScrollVideo: Video element not found');
        return;
      }
      
      // IMPORTANTE: Rimuovi le source non appropriate per il dispositivo corrente
      // Questo evita che il browser carichi video non necessari
      this.removeInappropriateSources();
      
      // Setup video con priorità alta di caricamento
      this.video.muted = true;
      this.video.playsInline = true;
      this.video.preload = 'auto';
      
      // FORZA IL CARICAMENTO IMMEDIATO: imposta preload prima di qualsiasi altra operazione
      // Questo assicura che il browser inizi a scaricare il video subito
      if (this.video.preload !== 'auto') {
        this.video.preload = 'auto';
      }
      
      // Forza il caricamento immediato chiamando load() subito
      // Questo è importante anche per browser non-Safari
      try {
        this.video.load();
      } catch (e) {
        console.warn('SimpleScrollVideo: load() failed:', e);
      }
      
      // if (this.config.debug) {
      //   const browserName = this.browser.isSafari ? 'Safari' : this.browser.isChrome ? 'Chrome' : 'Other';
      //   console.log('SimpleScrollVideo initialized:', {
      //     browser: browserName,
      //     config: this.config,
      //     readyState: this.video.readyState
      //   });
      // }
      
      // Calcola il frame iniziale in secondi
      const initialTime = (this.config.rewindToFrame || 0) / this.config.fps;
      
      // Safari richiede che il video sia caricato prima di manipolare currentTime
      if (this.browser.isSafari) {
        console.log('🎬 Safari detected - waiting for video to be ready');
        
        // Assicurati che il video sia visibile (fix per transizioni Swup)
        this.video.style.opacity = '1';
        this.video.style.visibility = 'visible';
        this.video.style.display = 'block';
        
        // Forza il caricamento del video per Safari (già fatto sopra, ma lo rifacciamo per sicurezza)
        this.video.load();
        
        // Aspetta che il video sia pronto prima di inizializzare
        const onCanPlay = () => {
          console.log('🎬 Safari video ready - initializing to frame', this.config.rewindToFrame, '(', initialTime, 's)');
          this.video.currentTime = initialTime;
          // CRITICO per mobile: attiva il video con play() altrimenti i frame non si vedono
          this.activateVideoForMobile();
          this.startScrollListener();
          this.video.removeEventListener('canplay', onCanPlay);
        };
        
        // Aggiungi anche listener per loadedmetadata (più precoce di canplay)
        const onLoadedMetadata = () => {
          console.log('🎬 Safari video metadata loaded - readyState:', this.video.readyState);
          if (this.video.readyState >= 2) {
            this.video.removeEventListener('loadedmetadata', onLoadedMetadata);
            // Se abbiamo già abbastanza dati, procedi
            if (!this.video.currentTime || this.video.currentTime === 0) {
              this.video.currentTime = initialTime;
            }
            this.activateVideoForMobile();
            this.startScrollListener();
          }
        };
        
        if (this.video.readyState >= 2) {
          // Video già pronto
          console.log('🎬 Safari video already ready - setting to frame', this.config.rewindToFrame);
          this.video.currentTime = initialTime;
          this.activateVideoForMobile();
          this.startScrollListener();
        } else {
          // Aspetta che sia pronto - usa loadedmetadata per risposta più rapida
          this.video.addEventListener('loadedmetadata', onLoadedMetadata);
          this.video.addEventListener('canplay', onCanPlay);
          
          // Timeout di sicurezza per Safari (ridotto da 2000ms a 1500ms per essere più reattivi)
          setTimeout(() => {
            if (this.video.readyState < 2) {
              console.warn('🎬 Safari video timeout - forcing initialization');
              this.video.removeEventListener('loadedmetadata', onLoadedMetadata);
              this.video.removeEventListener('canplay', onCanPlay);
              this.video.currentTime = initialTime;
              this.activateVideoForMobile();
              this.startScrollListener();
            }
          }, 1500);
        }
      } else {
        // Chrome e altri browser: inizializzazione immediata
        // Ma aspetta comunque che i metadata siano caricati per evitare errori
        const initNonSafari = () => {
          if (this.video.readyState >= 1) {
            // Abbiamo almeno i metadata
            this.video.currentTime = initialTime;
            this.activateVideoForMobile();
            this.startScrollListener();
          } else {
            // Aspetta i metadata
            const onLoadedMetadata = () => {
              this.video.currentTime = initialTime;
              this.activateVideoForMobile();
              this.startScrollListener();
              this.video.removeEventListener('loadedmetadata', onLoadedMetadata);
            };
            this.video.addEventListener('loadedmetadata', onLoadedMetadata);
            
            // Timeout di sicurezza
            setTimeout(() => {
              if (this.video.readyState < 1) {
                console.warn('🎬 Non-Safari video timeout - forcing initialization');
                this.video.removeEventListener('loadedmetadata', onLoadedMetadata);
                this.video.currentTime = initialTime;
                this.activateVideoForMobile();
                this.startScrollListener();
              }
            }, 1000);
          }
        };
        
        // Prova inizializzazione immediata
        initNonSafari();
      }
    }
    
    // Rimuove le source video non appropriate per il dispositivo corrente
    removeInappropriateSources() {
      if (!this.video) return;
      
      const width = window.innerWidth || document.documentElement.clientWidth;
      const isMobile = width < 768;
      const isTablet = width >= 768 && width <= 1024;
      
      // Trova tutte le source
      const sources = Array.from(this.video.querySelectorAll('source'));
      
      sources.forEach((source) => {
        const media = source.getAttribute('media');
        
        // Se la source ha una media query, verifica se corrisponde al dispositivo corrente
        if (media) {
          const mediaMatch = window.matchMedia(media);
          
          // Se la media query non corrisponde, rimuovi la source
          if (!mediaMatch.matches) {
            source.remove();
            //console.log('🗑️ Rimossa source non appropriata:', source.src, 'media:', media);
          }
        } else {
          // Source senza media query: rimuovile solo se ci sono source specifiche per dispositivo
          // Questo evita di rimuovere i fallback quando non ci sono alternative
          const hasSpecificSources = sources.some(s => s.getAttribute('media') !== null);
          if (hasSpecificSources) {
            source.remove();
            //console.log('🗑️ Rimossa source fallback (esistono source specifiche):', source.src);
          }
        }
      });
      
      //console.log('✅ Source appropriate mantenute per dispositivo:', isMobile ? 'mobile' : (isTablet ? 'tablet' : 'desktop'));
    }
    
    startScrollListener() {
      let ticking = false;
      
      const scrollHandler = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            this.handleScroll();
            ticking = false;
          });
          ticking = true;
        }
      };
      
      window.addEventListener('scroll', scrollHandler, { passive: true });
      
      // Initial check
      this.handleScroll();
    }
    
    handleScroll() {
      const scrollY = window.pageYOffset || document.documentElement.scrollTop;
      const scrollDelta = Math.abs(scrollY - this.state.lastScrollY);
      this.state.lastScrollY = scrollY;
      
      // Se stiamo hovering un pin, permettiamo lo scroll ma NON manipoliamo il video
      // Il pin manterrà la sua posizione relativa al video, quindi scrollando si uscirà naturalmente dall'hover
      if (this.state.isHovering) {
        // Lo scroll è permesso ma non influenza il video
        return;
      }
      
      // Controlla se abbiamo superato la soglia di scroll
      if (scrollY >= this.config.scrollTrigger && !this.state.isScrollActive) {
        this.startVideoPlayback();
      } else if (scrollY < this.config.scrollTrigger && this.state.isScrollActive) {
        // Se lockAfterFirstPlay è attivo e il video ha già raggiunto la fine, NON resettare
        if (this.config.lockAfterFirstPlay && this.state.hasReachedEnd) {
          // if (this.config.debug) {
          //   console.log('🔒 Video locked alla fine - non resetto');
          // }
          return; // Non fare nulla, lascia il video alla fine
        }
        this.resetVideo();
      }
      
      // if (this.config.debug && scrollDelta > 5) {
      //   console.log('Scroll:', scrollY, 'Active:', this.state.isScrollActive, 'Video time:', this.video.currentTime);
      // }
    }
    
    startVideoPlayback() {
      if (this.state.isScrollActive) return;
      
      this.state.isScrollActive = true;
      this.state.currentTargetTime = this.config.videoDuration;
      
      // if (this.config.debug) {
      //   console.log('Starting video playback to end');
      // }
      
      // Avvia il video verso la fine
      this.playToEnd();
    }
    
    resetVideo() {
      this.state.isScrollActive = false;
      
      // Calcola il target iniziale basato su rewindToFrame
      const startTarget = (this.config.rewindToFrame || 0) / this.config.fps;
      this.state.currentTargetTime = startTarget;
      
      // Resetta anche il flag hasReachedEnd per permettere la prossima animazione lenta
      this.state.hasReachedEnd = false;
      
      // if (this.config.debug) {
      //   console.log('Resetting video to frame', this.config.rewindToFrame, '(', startTarget, 's) - nascondo pin e resetto hasReachedEnd');
      // }
      
      // Nascondi i pin quando si torna indietro
      this.hidePins();
      
      this.smoothSeekTo(startTarget);
    }
    
    playToEnd() {
      if (this.state.isHovering) return; // Non giocare se stiamo hovering
      
      const targetTime = this.config.videoDuration;
      
      // Se è la prima volta che il video va alla fine, usa firstPlaybackSmoothness (più lenta)
      // Altrimenti usa returnSmoothness (velocità normale)
      const smoothness = !this.state.hasReachedEnd 
        ? this.config.firstPlaybackSmoothness 
        : this.config.returnSmoothness;
      
      // if (this.config.debug && !this.state.hasReachedEnd) {
      //   console.log('🎬 Prima animazione verso la fine - usando firstPlaybackSmoothness:', smoothness);
      // }
      
      this.smoothSeekTo(targetTime, smoothness);
    }
    
    smoothSeekTo(targetTime, smoothness = null) {
      if (this.state.animationFrame) {
        cancelAnimationFrame(this.state.animationFrame);
      }
      
      const smoothValue = smoothness !== null ? smoothness : this.config.smoothness;
      
      const animate = () => {
        const currentTime = this.video.currentTime;
        const diff = targetTime - currentTime;
        
        if (Math.abs(diff) < 0.016) { // Circa 1 frame a 60fps
          this.video.currentTime = targetTime;
          
          // Controlla se abbiamo raggiunto la fine del video per la prima volta
          if (!this.state.hasReachedEnd && Math.abs(targetTime - this.config.videoDuration) < 0.1) {
            this.state.hasReachedEnd = true;
            //console.log('🎬 Video ha raggiunto la fine per la prima volta - mostro i pin');
            this.showPins();
          }
          
          return;
        }
        
        // Smooth lerp verso il target
        const newTime = currentTime + (diff * smoothValue);
        this.video.currentTime = Math.max(0, Math.min(this.config.videoDuration, newTime));
        
        // Mostra i pin quando il video ha raggiunto circa il 85% del percorso (leggermente prima della fine)
        // Questo crea un effetto più fluido dove i pin appaiono mentre il video sta ancora andando verso la fine
        const threshold = this.config.videoDuration * 0.85; // 85% del percorso
        if (!this.state.hasReachedEnd && newTime >= threshold && Math.abs(targetTime - this.config.videoDuration) < 0.1) {
          this.state.hasReachedEnd = true;
          //console.log('🎬 Video ha raggiunto l\'85% - mostro i pin anticipatamente');
          this.showPins();
        }
        
        this.state.animationFrame = requestAnimationFrame(animate);
      };
      
      animate();
    }
    
    getPinTransforms() {
      if (typeof window === 'undefined') {
        return {
          enterStart: 'translate(-50%, -125%)',
        enterEnd: 'translate(-50%, -25%)',
        exit: 'translate(-50%, -75%)'
        };
      }
      
      const isMobile = window.innerWidth <= 1024 || 'ontouchstart' in window;
      const scale = isMobile ? ' scale(0.71)' : '';
      
      return {
        enterStart: `translate(-50%, -125%)${scale}`,
      enterEnd: `translate(-50%, -25%)${scale}`,
      exit: `translate(-50%, -75%)${scale}`
      };
    }
  
  clearPendingPinState(pin) {
    if (!pin) return;
    
    if (pin._pendingHideTimeout) {
      clearTimeout(pin._pendingHideTimeout);
      pin._pendingHideTimeout = null;
    }
    if (pin._pendingShowTimeout) {
      clearTimeout(pin._pendingShowTimeout);
      pin._pendingShowTimeout = null;
    }
    if (pin._pendingFocusTimeout) {
      clearTimeout(pin._pendingFocusTimeout);
      pin._pendingFocusTimeout = null;
    }
    if (pin._pinHideHandler) {
      pin.removeEventListener('transitionend', pin._pinHideHandler);
      pin._pinHideHandler = null;
    }
  }
  
  updatePinState(pin, targetState, options = {}) {
    if (!pin) return;
    
    const { force = false, delayOnHide = true, animateFromTop: animateOverride = null } = options;
    const hadPendingHide = !!pin._pendingHideTimeout;
    const hadPendingShow = !!pin._pendingShowTimeout || !!pin._pendingFocusTimeout;
    
    const applyState = () => {
      this.clearPendingPinState(pin);
      const previousState = getPinStateAttr(pin) || PIN_STATE_HIDDEN;
      if (!force && previousState === targetState) {
        return;
      }
      
      const { enterStart, enterEnd, exit } = this.getPinTransforms();
      const isShowingState = targetState === PIN_STATE_VISIBLE || targetState === PIN_STATE_ACTIVE;
      const shouldAnimateFromTop = animateOverride !== null
        ? animateOverride
        : (isShowingState && (force || previousState === PIN_STATE_HIDDEN || hadPendingHide || hadPendingShow));
      
      const animateIn = () => {
        pin.style.transition = 'opacity 0.35s ease, transform 0.45s ease';
        
        if (shouldAnimateFromTop) {
          pin.style.opacity = '0';
          pin.style.transform = enterStart;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              pin.style.opacity = '1';
              pin.style.transform = enterEnd;
            });
          });
        } else {
          pin.style.opacity = '1';
          pin.style.transform = enterEnd;
        }
      };
      
      if (targetState === PIN_STATE_ACTIVE) {
        pin.classList.add('pin-visible');
        pin.classList.add('pin--active');
        pin.classList.remove('pin--hidden');
        animateIn();
        setPinStateAttr(pin, PIN_STATE_ACTIVE);
        return;
      }
      
      if (targetState === PIN_STATE_VISIBLE) {
        pin.classList.add('pin-visible');
        pin.classList.remove('pin--active');
        pin.classList.remove('pin--hidden');
        animateIn();
        setPinStateAttr(pin, PIN_STATE_VISIBLE);
        return;
      }
      
      // targetState === PIN_STATE_HIDDEN
      pin.classList.remove('pin--active');
      pin.classList.add('pin--hidden');
      pin.style.transition = 'opacity 0.3s ease, transform 0.4s ease';
      pin.style.transform = exit;
      pin.style.opacity = '0';
      
      const handleTransitionEnd = (event) => {
        if (event.propertyName !== 'opacity') return;
        pin.removeEventListener('transitionend', handleTransitionEnd);
        pin._pinHideHandler = null;
        if (getPinStateAttr(pin) !== PIN_STATE_HIDDEN) {
          return;
        }
        pin.classList.remove('pin-visible');
      };
      
      pin._pinHideHandler = handleTransitionEnd;
      pin.addEventListener('transitionend', handleTransitionEnd);
      setPinStateAttr(pin, PIN_STATE_HIDDEN);
    };
    
    if (targetState === PIN_STATE_HIDDEN && !force && delayOnHide) {
      this.clearPendingPinState(pin);
      pin._pendingHideTimeout = setTimeout(applyState, PIN_HIDE_DELAY_MS);
      return;
    }
    
    this.clearPendingPinState(pin);
    applyState();
  }
    
    // Metodo per mostrare i pin con animazione stagger
    showPins() {
      if (this.state.pinsVisible || !this.pinsContainer) return;

      this.state.pinsVisible = true;
      const pins = this.pinsContainer.querySelectorAll('.pin');

      if (pins.length === 0) {
        //console.warn('🎬 showPins: nessun pin trovato');
        return;
      }

    pins.forEach((pin, index) => {
      if (pin._pendingShowTimeout) {
        clearTimeout(pin._pendingShowTimeout);
      }
      
      const delay = index * PIN_SHOW_STAGGER_MS;
      
      pin._pendingShowTimeout = setTimeout(() => {
        this.updatePinState(pin, PIN_STATE_VISIBLE, { force: true, delayOnHide: false, animateFromTop: true });
        pin._pendingShowTimeout = null;
      }, delay);
      });
    }
    
    // Metodo per nascondere i pin con animazione stagger (inversa)
    hidePins() {
      if (!this.state.pinsVisible || !this.pinsContainer) return;

      this.state.pinsVisible = false;
      const pins = this.pinsContainer.querySelectorAll('.pin');

      if (pins.length === 0) {
        //console.warn('🎬 hidePins: nessun pin trovato');
        return;
      }

      pins.forEach((pin) => {
        this.updatePinState(pin, PIN_STATE_HIDDEN, { force: true, delayOnHide: false });
      });
    }
    
    // Metodi per la gestione dei pin
    registerPin(pinElement, rewindTarget = 0) {
      if (!pinElement) return;
      
      const pinData = {
        element: pinElement,
        rewindTarget: Math.max(0, Math.min(this.config.videoDuration, rewindTarget)),
        isActive: false
      };
      
      this.pins.push(pinData);
      
      // Aggiungi event listeners
      pinElement.addEventListener('mouseenter', () => {
        //console.log('🎯 PIN HOVER START:', pinData.element.id);
        this.onPinHover(pinData);
      });
      pinElement.addEventListener('mouseleave', () => {
        //console.log('🎯 PIN HOVER END:', pinData.element.id);
        this.onPinLeave(pinData);
      });
      
      // if (this.config.debug) {
      //   console.log('Pin registered:', pinData);
      // }
    }
    
    onPinHover(pinData) {
      if (this.state.isHovering) {
        //console.log('🎯 HOVER BLOCKED - già hovering su altro pin');
        return; // Già in hover di un altro pin
      }
      
      this.state.isHovering = true;
      pinData.isActive = true;
      
      //console.log('🎯 PIN HOVER ACTIVATED - Rewind to:', pinData.rewindTarget, 'Current time:', this.video.currentTime);
      
      // Ferma qualsiasi animazione in corso
      if (this.state.animationFrame) {
        cancelAnimationFrame(this.state.animationFrame);
      }
      
      // Inizia rewind fluido verso il target
      this.startRewind(pinData.rewindTarget);
    }
    
    onPinLeave(pinData) {
      if (!pinData.isActive) {
        //console.log('🎯 LEAVE IGNORED - pin non era attivo');
        return;
      }
      
      this.state.isHovering = false;
      pinData.isActive = false;
      
      //console.log('🎯 PIN LEAVE ACTIVATED - Return to end, scroll active:', this.state.isScrollActive);
      
      // Stop rewind
      this.stopRewind();
      
      // Torna alla fine se lo scroll è attivo
      if (this.state.isScrollActive) {
        this.playToEnd();
      }
    }
    
    startRewind(targetTime) {
      //console.log('🎬 START REWIND to:', targetTime, 'from:', this.video.currentTime);
      this.stopRewind(); // Ferma eventuali rewind precedenti
      
      const startTime = this.video.currentTime;
      const distance = startTime - targetTime;
      
      // Usa un approccio smooth con easing per evitare scatti
      const rewindStep = () => {
        if (!this.state.isHovering) {
          return; // Stop se non stiamo più hovering
        }
        
        const currentTime = this.video.currentTime;
        
        // Rewind lineare a velocità costante
        if (currentTime <= targetTime) {
          this.video.currentTime = targetTime;
          return; // Raggiunto il target
        }
        
        // Velocità costante senza easing
        const newTime = Math.max(targetTime, currentTime - this.config.rewindSpeed);
        this.video.currentTime = newTime;
        
        // Continua il rewind
        this.state.rewindInterval = requestAnimationFrame(rewindStep);
      };
      
      this.state.rewindInterval = requestAnimationFrame(rewindStep);
    }
    
    stopRewind() {
      if (this.state.rewindInterval) {
        cancelAnimationFrame(this.state.rewindInterval);
        this.state.rewindInterval = null;
      }
    }
    
    // Cleanup
    destroy() {
      this.stopRewind();
      
      if (this.state.animationFrame) {
        cancelAnimationFrame(this.state.animationFrame);
      }
      
      // Rimuovi event listeners (se necessario)
      // window.removeEventListener('scroll', this.handleScroll);
      
      // if (this.config.debug) {
      //   console.log('SimpleScrollVideo destroyed');
      // }
    }
  }
  
  // Funzione helper per inizializzare il sistema
  export function initSimpleScrollVideo(videoSelector, config = {}) {
    const videoElement = typeof videoSelector === 'string' 
      ? document.querySelector(videoSelector) 
      : videoSelector;
      
    if (!videoElement) {
      //console.warn('SimpleScrollVideo: Video element not found');
      return null;
    }
    
    return new SimpleScrollVideo(videoElement, config);
  }
  
  // Funzione per creare i pin nel DOM dal pinsData
  export function renderPins(pinsData, pinsContainer, section, scrollVideoInstance = null) {
    if (!pinsContainer || !Array.isArray(pinsData) || pinsData.length === 0) {
      //console.warn('SimpleScrollVideo: Invalid pinsContainer or pinsData');
      return;
    }
    
    //console.log('📍 Rendering pins:', pinsData.length);
    
    // Salva il riferimento al pinsContainer nell'istanza del video (se fornita)
    if (scrollVideoInstance) {
      scrollVideoInstance.pinsContainer = pinsContainer;
    }
    
    // Pulisci container esistente
    pinsContainer.innerHTML = '';
    
    // Determina dispositivo: desktop (>1024px), tablet (768-1024px), mobile (<768px)
    // IMPORTANTE: controlla prima tablet, poi mobile, per evitare conflitti
    const width = window.innerWidth;
    const isTablet = width >= 768 && width <= 1024;
    const isMobile = width < 768 || (matchMedia('(pointer: coarse)').matches && !isTablet);
    const isTouch = isMobile || isTablet; // Per compatibilità con il resto del codice
    const transforms = scrollVideoInstance ? scrollVideoInstance.getPinTransforms() : null;
    
    // console.log('📍 [renderPins] Rilevamento dispositivo:', {
    //   width,
    //   isMobile,
    //   isTablet,
    //   isDesktop: !isMobile && !isTablet
    // });
    
    pinsData.forEach((pinData, index) => {
      // Crea elemento pin - SENZA pin-visible, sarà aggiunto dopo che il video raggiunge la fine
      const pin = document.createElement('div');
      pin.className = 'pin';
      pin.id = pinData.id || `pin-${index}`;
      pin.style.opacity = '0'; // Inizialmente invisibile
      
      // Log dei dati disponibili per questo pin
      // console.log(`📍 [renderPins] Pin ${pinData.id || index} - Dati disponibili:`, {
      //   x_desktop: pinData.x_desktop,
      //   y_desktop: pinData.y_desktop,
      //   x_tablet: pinData.x_tablet,
      //   y_tablet: pinData.y_tablet,
      //   x_mobile: pinData.x_mobile,
      //   y_mobile: pinData.y_mobile
      // });
      
      // Posiziona il pin in base al dispositivo
      // IMPORTANTE: controlla prima tablet, poi mobile, per dare priorità al tablet
      let x, y;
      if (isTablet) {
        // Tablet: usa tablet, fallback a desktop
        x = pinData.x_tablet ?? pinData.x_desktop;
        y = pinData.y_tablet ?? pinData.y_desktop;
        //console.log(`📍 [renderPins] Pin ${pinData.id || index} - TABLET: x=${x}%, y=${y}% (tablet: ${pinData.x_tablet}/${pinData.y_tablet}, desktop: ${pinData.x_desktop}/${pinData.y_desktop})`);
      } else if (isMobile) {
        // Mobile: usa mobile, fallback a tablet, poi desktop
        x = pinData.x_mobile ?? pinData.x_tablet ?? pinData.x_desktop;
        y = pinData.y_mobile ?? pinData.y_tablet ?? pinData.y_desktop;
        //console.log(`📍 [renderPins] Pin ${pinData.id || index} - MOBILE: x=${x}%, y=${y}% (mobile: ${pinData.x_mobile}/${pinData.y_mobile}, tablet: ${pinData.x_tablet}/${pinData.y_tablet}, desktop: ${pinData.x_desktop}/${pinData.y_desktop})`);
      } else {
        // Desktop: usa sempre desktop
        x = pinData.x_desktop;
        y = pinData.y_desktop;
        //console.log(`📍 [renderPins] Pin ${pinData.id || index} - DESKTOP: x=${x}%, y=${y}%`);
      }
      
      // Usa variabili CSS per essere coerenti con il CSS che usa var(--x) e var(--y)
      pin.style.setProperty('--x', `${x}%`);
      pin.style.setProperty('--y', `${y}%`);
      //console.log(`📍 [renderPins] Pin ${pinData.id || index} - CSS applicato: --x=${x}%, --y=${y}%`);
      
      // Applica transform con scala su mobile
      const initialTransform = transforms
        ? transforms.exit
        : isTouch
          ? 'translate(-50%, -25%) scale(0.71)'
          : 'translate(-50%, -25%)';
      
      pin.style.transform = initialTransform;
      if (isTouch) {
        pin.style.transformOrigin = 'center bottom';
      }
      pin.classList.add('pin--hidden');
      pin.setAttribute(PIN_STATE_ATTR, PIN_STATE_HIDDEN);
      
      // Salva i dati del pin sull'elemento per poter aggiornare le posizioni al resize
      pin._pinData = pinData;
      
      // Crea SVG pin icon
      const pinSvg = document.createElement('div');
      pinSvg.className = 'pin-svg';
      pinSvg.innerHTML = `
        <svg width="42" height="59" viewBox="0 0 42 59" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_1_14264)">
          <path d="M41.2031 20.572C41.2031 39.4844 20.683 58.4182 20.683 58.4182C20.683 58.4182 0.162964 39.4851 0.162964 20.572C0.162964 9.23793 9.3504 0.0493164 20.683 0.0493164C32.0157 0.0493164 41.2031 9.23793 41.2031 20.572Z" fill="#9D5024"/>
          <path d="M26.9207 19.1276L29.8712 12.3693L22.486 14.9146L17.6387 9.16943L16.323 16.3526L8.37097 16.8226L13.4564 21.6624C13.4564 21.6624 13.4421 21.6383 11.8035 29.3271L19.0277 26.232L23.8797 31.9695L25.1964 24.7786L32.995 23.7655L26.9198 19.1267L26.9207 19.1276Z" fill="#E5DBD3"/>
          </g>
          <defs>
          <clipPath id="clip0_1_14264">
          <rect width="41.0401" height="58.3682" fill="white" transform="translate(0.162964 0.0493164)"/>
          </clipPath>
          </defs>
        </svg>`;
      
      pin.appendChild(pinSvg);
      
      // Aggiungi logo cantina se presente
      if (pinData.logo) {
        const logo = document.createElement('img');
        logo.className = 'cantina-logo';
        logo.src = pinData.logo;
        logo.alt = pinData.title || 'Logo cantina';
        pin.appendChild(logo);
      }
      
      // Salva dati per eventi
      pin._pinData = pinData;
      
      pinsContainer.appendChild(pin);
      
      // Debug: verifica posizione e visibilità
      // console.log(`📍 Pin #${index} created:`, {
      //   id: pin.id,
      //   position: `${x}%, ${y}%`,
      //   visible: pin.classList.contains('pin-visible'),
      //   classes: pin.className
      // });
    });
    
    //console.log(`📍 Created ${pinsData.length} pins in DOM`);
    
    // Debug: verifica container
    // setTimeout(() => {
    //   const createdPins = pinsContainer.querySelectorAll('.pin');
    //   console.log(`📍 Verifica post-creation: ${createdPins.length} pins nel DOM`);
    //   createdPins.forEach(pin => {
    //     const rect = pin.getBoundingClientRect();
    //     console.log(`📍 Pin ${pin.id} rect:`, {
    //       visible: rect.width > 0 && rect.height > 0,
    //       position: `${rect.left}, ${rect.top}`,
    //       size: `${rect.width}x${rect.height}`
    //     });
    //   });
    // }, 100);
  }
  
  // Funzione per aggiornare le posizioni dei pin in base alla dimensione della finestra
  export function updatePinPositions(pinsContainer) {
    if (!pinsContainer) return;
    
    const pins = pinsContainer.querySelectorAll('.pin');
    if (pins.length === 0) return;
    
    // Determina dispositivo: desktop (>1024px), tablet (768-1024px), mobile (<768px)
    // IMPORTANTE: controlla prima tablet, poi mobile, per evitare conflitti
    const width = window.innerWidth;
    const isTablet = width >= 768 && width <= 1024;
    const isMobile = width < 768 || (matchMedia('(pointer: coarse)').matches && !isTablet);
    
    // console.log('📍 [updatePinPositions] Resize rilevato:', {
    //   width,
    //   isMobile,
    //   isTablet,
    //   isDesktop: !isMobile && !isTablet,
    //   pinsCount: pins.length
    // });
    
    pins.forEach((pin) => {
      // Recupera i dati originali del pin salvati durante il rendering
      const pinData = pin._pinData;
      if (!pinData) {
        //console.warn(`📍 [updatePinPositions] Pin ${pin.id} - Nessun _pinData trovato!`);
        return;
      }
      
      // console.log(`📍 [updatePinPositions] Pin ${pin.id} - Dati disponibili:`, {
      //   x_desktop: pinData.x_desktop,
      //   y_desktop: pinData.y_desktop,
      //   x_tablet: pinData.x_tablet,
      //   y_tablet: pinData.y_tablet,
      //   x_mobile: pinData.x_mobile,
      //   y_mobile: pinData.y_mobile
      // });
      
      // Calcola le nuove posizioni in base al dispositivo
      // IMPORTANTE: controlla prima tablet, poi mobile, per dare priorità al tablet
      let x, y;
      if (isTablet) {
        // Tablet: usa tablet, fallback a desktop
        x = pinData.x_tablet ?? pinData.x_desktop;
        y = pinData.y_tablet ?? pinData.y_desktop;
        //console.log(`📍 [updatePinPositions] Pin ${pin.id} - TABLET: x=${x}%, y=${y}% (tablet: ${pinData.x_tablet}/${pinData.y_tablet}, desktop: ${pinData.x_desktop}/${pinData.y_desktop})`);
      } else if (isMobile) {
        // Mobile: usa mobile, fallback a tablet, poi desktop
        x = pinData.x_mobile ?? pinData.x_tablet ?? pinData.x_desktop;
        y = pinData.y_mobile ?? pinData.y_tablet ?? pinData.y_desktop;
        //console.log(`📍 [updatePinPositions] Pin ${pin.id} - MOBILE: x=${x}%, y=${y}%`);
      } else {
        // Desktop: usa sempre desktop
        x = pinData.x_desktop;
        y = pinData.y_desktop;
        //console.log(`📍 [updatePinPositions] Pin ${pin.id} - DESKTOP: x=${x}%, y=${y}%`);
      }
      
      // Aggiorna le variabili CSS
      pin.style.setProperty('--x', `${x}%`);
      pin.style.setProperty('--y', `${y}%`);
      //console.log(`📍 [updatePinPositions] Pin ${pin.id} - CSS aggiornato: --x=${x}%, --y=${y}%`);
    });
  }
  
  // Funzione per registrare automaticamente i pin di una sezione con overlay
  export function registerScrollMapPins(scrollVideoInstance, pinsContainer) {
    if (!scrollVideoInstance || !pinsContainer) {
      console.warn('SimpleScrollVideo: Invalid scrollVideoInstance or pinsContainer');
      return;
    }
    
    // Salva il riferimento al pinsContainer nell'istanza del video
    scrollVideoInstance.pinsContainer = pinsContainer;
    
    const pins = pinsContainer.querySelectorAll('.pin');
    
    // Trova l'overlay globale
    const wrapper = pinsContainer.parentElement; // map-pins-wrapper
    const globalOverlay = wrapper?.querySelector('.pin-hover-overlay');
    
    if (!globalOverlay) {
      console.error('[SimpleScrollVideo] Overlay globale non trovato!');
      return;
    }
    
    const overlayArrow = globalOverlay.querySelector('.overlay-arrow');
    const overlayLeft = globalOverlay.querySelector('.overlay-left');
    const overlayRight = globalOverlay.querySelector('.overlay-right');
    const overlayCenter = globalOverlay.querySelector('.overlay-center');
    const clickableOverlayElements = [overlayArrow, overlayLeft, overlayRight, overlayCenter].filter(Boolean);

    const navigateToLink = (link, pinDataOverride = null) => {
      if (!link) return;

      let resolvedLink = link;

      if (typeof window !== 'undefined') {
        try {
          const url = new URL(link, window.location.origin);
          resolvedLink = url.href;

          if (url.origin === window.location.origin) {
            updateNavMetaForLink(resolvedLink);
            prepareCantinaTransition(resolvedLink, pinDataOverride || currentPinData);
          }
        } catch (err) {
          console.warn('SimpleScrollVideo: unable to resolve link for navigation', link, err);
        }
      }

      if (window?.swup) {
        window.swup.navigate(resolvedLink);
        return;
      }

      window.location.href = resolvedLink;
    };
    
    // Variabili di stato per gestire l'overlay
    let currentPin = null;
    let currentHoverData = null;
    let currentPinIndex = null;
    let currentPinData = null;
    let hideTimeout = null;
    let hideOverlayTimeouts = []; // timeout interni di hideOverlay, da cancellare se l'overlay viene riaperto
    let globalOverlayVisible = false;
    let arrowHasLink = false;
    
    // Traccia l'ultimo touch per evitare conflitti
    let lastGlobalTouchTime = 0;
    let blockMouseEvents = false;
    let blockMouseTimeout = null;
    

    const getPins = () => Array.from(pinsContainer.querySelectorAll('.pin'));

    const clearScheduledState = (pin) => {
      if (pin._pendingFocusTimeout) {
        clearTimeout(pin._pendingFocusTimeout);
        pin._pendingFocusTimeout = null;
      }
    };

    const schedulePinState = (pin, state, delay = 0, options = {}) => {
      if (!pin) return;
      clearScheduledState(pin);
      scrollVideoInstance.clearPendingPinState(pin);
      if (delay > 0) {
        pin._pendingFocusTimeout = setTimeout(() => {
          scrollVideoInstance.updatePinState(pin, state, options);
          pin._pendingFocusTimeout = null;
        }, delay);
      } else {
        scrollVideoInstance.updatePinState(pin, state, options);
      }
    };

    const setFocusedPin = (activePin) => {
      if (!activePin) return;
      const pinsArray = getPins();
      pinsArray.forEach((pin, index) => {
        const baseDelay = index * PIN_SHOW_STAGGER_MS;
        if (pin === activePin) {
          schedulePinState(pin, PIN_STATE_ACTIVE, 0, { force: false, delayOnHide: false, animateFromTop: false });
        } else {
          schedulePinState(pin, PIN_STATE_HIDDEN, baseDelay, { force: true, delayOnHide: false });
        }
      });
    };

    const resetPinsFocus = () => {
      const shouldBeVisible = !!(scrollVideoInstance.state && scrollVideoInstance.state.pinsVisible);
      const pinsArray = getPins();
      pinsArray.forEach((pin, index) => {
        const delay = index * PIN_SHOW_STAGGER_MS;
        if (shouldBeVisible) {
          schedulePinState(pin, PIN_STATE_VISIBLE, delay, { force: true, delayOnHide: false, animateFromTop: false });
        } else {
          schedulePinState(pin, PIN_STATE_HIDDEN, delay, { force: true, delayOnHide: false });
        }
      });
    };
    
    // Variabili per gestire lo scroll durante hover
    let lastScrollY = window.pageYOffset || document.documentElement.scrollTop;
    let scrollListener = null;
    
    // Funzione per mostrare l'overlay con animazioni
    const showOverlay = (pinElement, hoverData, pinIndex, pinLink = null) => {
      if (!hoverData || (!hoverData.img_italia && !hoverData.img_regione && !hoverData.logo_cantina)) {
        console.warn('📍 showOverlay: nessun dato hover disponibile', hoverData);
        return;
      }
      
      // IMPORTANTE: Cancella tutti i timeout esistenti per evitare conflitti
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }
      // Cancella un eventuale hideOverlay ancora in corso, altrimenti rimuoverebbe is-visible
      // dall'overlay appena riaperto (Chrome desktop: leave/enter rapidi su cambio layout)
      hideOverlayTimeouts.forEach(timeout => clearTimeout(timeout));
      hideOverlayTimeouts = [];

      // console.log('📍 showOverlay chiamato:', {
      //   pinIndex,
      //   hasLogoCantina: !!hoverData.logo_cantina,
      //   hasImgItalia: !!hoverData.img_italia,
      //   hasImgRegione: !!hoverData.img_regione,
      //   hasRegioneText: !!hoverData.regione_txt,
      //   hasCategorie: !!hoverData.categorie
      // });
      
      const pinRect = pinElement.getBoundingClientRect();
      const isMobile = window.innerWidth <= 1024;
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      pinElement.classList.add('pin-hovering');
      
      // SVG della freccia
      const arrowSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="45" height="46" fill="none">
          <circle cx="22.206" cy="22.206" r="22.206" fill="#9D5024" transform="matrix(1 0 0 -1 .418 45.216)"/>
          <g clip-path="url(#arrow_overlay_${pinIndex})">
            <path fill="#e5dbd3" stroke="#e5dbd3" stroke-width=".25" d="m29.66 16.145.017.144.101.567.089.372c.107.41.27.92.522 1.45.823 1.732 2.137 2.845 3.912 3.313l.32.085-.153.075v1.19l-.09.026-.08.023-.001-.001c-1.766.51-3.077 1.74-3.907 3.659h.001c-.252.59-.419 1.16-.527 1.614l-.09.413c-.052.276-.083.499-.1.635-.01.083-.017.127-.017.157v.17h-.12l-.01.11h-.115l-.089-.005-.357-.022a2.428 2.428 0 0 1-.431-.054.462.462 0 0 1-.139-.055.182.182 0 0 1-.088-.153v-.094l.023-.211v-.001a11.365 11.365 0 0 1 .82-3.035c.56-1.304 1.334-2.358 2.303-3.146H9.644v-1.357h21.653c-.893-.704-1.614-1.627-2.144-2.751a9.64 9.64 0 0 1-.827-2.794v-.001a2.443 2.443 0 0 0-.024-.18l-.003-.013v-.096c0-.08.052-.129.09-.154a.483.483 0 0 1 .14-.056c.102-.027.248-.044.43-.056l.375-.028.06-.003h.01v-.001l.123-.009.134-.009v.282Z"/>
          </g>
          <defs><clipPath id="arrow_overlay_${pinIndex}"><path fill="#fff" d="M9.768 15.997h24.58v14.025H9.768z"/></clipPath></defs>
        </svg>
      `;
      
      // Popola la freccia (con o senza link)
      if (pinLink) {
        overlayArrow.innerHTML = `
          <a href="${pinLink}" class="arrow-link" data-swup-animation="overlay">
            ${arrowSvg}
          </a>
        `;
        overlayArrow.classList.add('has-link');
        arrowHasLink = true;
      } else {
        overlayArrow.innerHTML = arrowSvg;
        overlayArrow.classList.remove('has-link');
        arrowHasLink = false;
      }
      
      // Posiziona la freccia
      overlayArrow.style.top = `${pinRect.top + pinRect.height / 2}px`;
      overlayArrow.style.left = `${pinRect.left + pinRect.width / 2}px`;
      
      // Applica transform con scala su mobile
      if (isTouch || isMobile) {
        overlayArrow.style.transform = 'translate(-50%, -50%) scale(0.71)';
        overlayArrow.style.transformOrigin = 'center center';
      } else {
        overlayArrow.style.transform = 'translate(-50%, -50%)';
      }
      
      // Su mobile/touch: mostra SOLO overlay center (logo cantina)
      // Su desktop: mostra tutto
      if (isTouch || isMobile) {
        // MOBILE: Nascondi left e right
        overlayLeft.innerHTML = '';
        overlayRight.innerHTML = '';
      } else {
        // DESKTOP: Mostra tutto
        // Popola overlay left (immagine Italia)
        overlayLeft.innerHTML = hoverData.img_italia ? `<img src="${hoverData.img_italia}" alt="Immagine Italia" loading="lazy" />` : '';
        
        // Popola overlay right (immagine regione + testi)
        let overlayRightContent = '';
        if (hoverData.img_regione) {
          overlayRightContent += `<img src="${hoverData.img_regione}" alt="Immagine Regione" loading="lazy" />`;
        }
        if (hoverData.regione_txt || hoverData.categorie) {
          overlayRightContent += '<div class="overlay-right-text">';
          if (hoverData.regione_txt) {
            overlayRightContent += `<p class="regione-txt">${hoverData.regione_txt}</p>`;
          }
          if (hoverData.categorie) {
            overlayRightContent += `<p class="categorie-txt">${hoverData.categorie}</p>`;
          }
          overlayRightContent += '</div>';
        }
        overlayRight.innerHTML = overlayRightContent;
      }
      
      // Popola overlay center (logo cantina)
      if (hoverData.logo_cantina) {
        if (pinLink) {
          overlayCenter.innerHTML = `
            <a href="${pinLink}" class="cantina-overlay-link" data-swup-animation="overlay">
              <img src="${hoverData.logo_cantina}" alt="Logo Cantina" loading="lazy" />
            </a>
          `;
        } else {
          overlayCenter.innerHTML = `<img src="${hoverData.logo_cantina}" alt="Logo Cantina" loading="lazy" />`;
        }
      } else {
        overlayCenter.innerHTML = '';
      }

      if (pinLink) {
        const overlayPinData = pinElement?._pinData || currentPinData || null;
        const arrowAnchor = overlayArrow.querySelector('a');
        if (arrowAnchor) {
          arrowAnchor.addEventListener('click', (event) => {
            event.preventDefault();
            navigateToLink(pinLink, overlayPinData);
          });
        }

        const centerAnchor = overlayCenter.querySelector('a');
        if (centerAnchor) {
          centerAnchor.addEventListener('click', (event) => {
            event.preventDefault();
            navigateToLink(pinLink, overlayPinData);
          });
        }
      }
      
      // Posizionamento degli elementi
      if (isTouch || isMobile) {
        // MOBILE: Solo overlay center, posizionato sopra il pin (più in basso)
        if (hoverData.logo_cantina) {
          overlayCenter.style.top = `${pinRect.top - 10}px`;
          overlayCenter.style.left = `${pinRect.left + pinRect.width / 2}px`;
          overlayCenter.style.transform = 'translate(-50%, -100%)';
        }
      } else {
        // DESKTOP: Posizionamento completo left, right, center
        if (hoverData.img_italia) {
          overlayLeft.style.top = `${pinRect.top + pinRect.height / 2}px`;
          overlayLeft.style.left = `${pinRect.left - 120}px`;
          overlayLeft.style.transform = 'translate(0, -50%)';
        }
        if (hoverData.img_regione) {
          overlayRight.style.top = `${pinRect.top + pinRect.height / 2}px`;
          overlayRight.style.left = `${pinRect.right + 30}px`;
          overlayRight.style.transform = 'translate(0, -50%)';
        }
        if (hoverData.logo_cantina) {
          overlayCenter.style.top = `${pinRect.top - 30}px`;
          overlayCenter.style.left = `${pinRect.left + pinRect.width / 2}px`;
          overlayCenter.style.transform = 'translate(-50%, -100%)';
        }
      }
      
      // Reset posizioni iniziali per animazione
      overlayArrow.style.opacity = '0';
      overlayLeft.style.opacity = '0';
      overlayRight.style.opacity = '0';
      overlayCenter.style.opacity = '0';
      
      const leftImg = overlayLeft.querySelector('img');
      const rightImg = overlayRight.querySelector('img');
      const centerImg = overlayCenter.querySelector('img');
      const rightText = overlayRight.querySelector('.overlay-right-text');
      
      if (leftImg) leftImg.style.transform = 'translateX(-30px)';
      if (rightImg) rightImg.style.transform = 'translateX(30px)';
      if (centerImg) centerImg.style.transform = 'translateY(-30px)';
      if (rightText) {
        rightText.style.opacity = '0';
        rightText.style.transform = 'translateY(20px)';
      }
      
      // Nascondi cursore custom
      const customCursor = document.getElementById('magic-cursor');
      if (customCursor) customCursor.style.opacity = '0';
      
      // Mostra overlay
      globalOverlay.classList.add('is-visible');
      globalOverlay.style.opacity = '1';
      globalOverlay.style.visibility = 'visible';
      globalOverlayVisible = true;
      //console.log('👁️ Overlay mostrato');
      
      // Attiva il listener per chiudere l'overlay scrollando
      lastScrollY = window.pageYOffset || document.documentElement.scrollTop;
      enableScrollExitOnHover();
      
      // Cancella timeout precedenti
      if (window.__overlayAnimationTimeouts) {
        window.__overlayAnimationTimeouts.forEach(timeout => clearTimeout(timeout));
        window.__overlayAnimationTimeouts = [];
      } else {
        window.__overlayAnimationTimeouts = [];
      }
      
      // Animazioni stagger
      const staggerDelay = 80;
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Freccia
          const t1 = setTimeout(() => {
            if (globalOverlay.classList.contains('is-visible')) {
              overlayArrow.style.opacity = '1';
            }
          }, 0);
          window.__overlayAnimationTimeouts.push(t1);
          
          // Left
          const t2 = setTimeout(() => {
            if (globalOverlay.classList.contains('is-visible')) {
              overlayLeft.style.opacity = '1';
              if (leftImg) leftImg.style.transform = 'translateX(0)';
            }
          }, staggerDelay);
          window.__overlayAnimationTimeouts.push(t2);
          
          // Right
          const t3 = setTimeout(() => {
            if (globalOverlay.classList.contains('is-visible')) {
              overlayRight.style.opacity = '1';
              if (rightImg) rightImg.style.transform = 'translateX(0)';
              if (rightText) {
                const t4 = setTimeout(() => {
                  if (globalOverlay.classList.contains('is-visible')) {
                    rightText.style.opacity = '1';
                    rightText.style.transform = 'translateY(0)';
                  }
                }, 100);
                window.__overlayAnimationTimeouts.push(t4);
              }
            }
          }, staggerDelay * 2);
          window.__overlayAnimationTimeouts.push(t3);
          
          // Center
          const t5 = setTimeout(() => {
            if (globalOverlay.classList.contains('is-visible')) {
              overlayCenter.style.opacity = '1';
              if (centerImg) centerImg.style.transform = 'translateY(0)';
            }
          }, staggerDelay * 3);
          window.__overlayAnimationTimeouts.push(t5);
        });
      });
    };
    
    // Funzione per attivare il listener di scroll durante hover
    const enableScrollExitOnHover = () => {
      // Su mobile/touch non attiviamo questo comportamento
      const isMobile = window.innerWidth <= 1024;
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      if (isMobile || isTouch) {
        console.log('📱 Mobile/Touch device - scroll exit disabilitato');
        return;
      }
      
      // Rimuovi listener precedente se esiste
      if (scrollListener) {
        window.removeEventListener('scroll', scrollListener);
      }
      
      // Crea nuovo listener
      scrollListener = () => {
        const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
        const scrollDelta = Math.abs(currentScrollY - lastScrollY);
        
        // Se lo scroll si è mosso di più di 5px, chiudi l'overlay
        if (scrollDelta > 5 && globalOverlay.classList.contains('is-visible')) {
          //console.log('📜 Scroll rilevato durante hover - chiudo overlay');
          
          // Forza chiusura overlay
          hideOverlay();
          currentPin = null;
          currentHoverData = null;
          currentPinIndex = null;
          currentPinData = null;
          if (hideTimeout) {
            clearTimeout(hideTimeout);
            hideTimeout = null;
          }
          
          // Stop rewind e riprendi video
          scrollVideoInstance.stopRewind();
          scrollVideoInstance.state.isHovering = false;
          
          const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
          if (currentScrollY >= scrollVideoInstance.config.scrollTrigger) {
            scrollVideoInstance.playToEnd();
            resetPinsFocus();
          } else {
            scrollVideoInstance.resetVideo();
            resetPinsFocus();
          }
        }
        
        lastScrollY = currentScrollY;
      };
      
      window.addEventListener('scroll', scrollListener, { passive: true });
      console.log('✅ Scroll exit listener attivato');
    };
    
    // Funzione per disattivare il listener di scroll
    const disableScrollExitOnHover = () => {
      if (scrollListener) {
        window.removeEventListener('scroll', scrollListener);
        scrollListener = null;
        console.log('❌ Scroll exit listener disattivato');
      }
    };
    
    // Funzione per nascondere l'overlay
    const hideOverlay = () => {
      // Disattiva il listener di scroll
      disableScrollExitOnHover();
      
      // Salva riferimento al pin corrente prima che venga resettato
      const pinToRestore = currentPin;
      
      // Cancella timeout animazioni
      if (window.__overlayAnimationTimeouts) {
        window.__overlayAnimationTimeouts.forEach(timeout => clearTimeout(timeout));
        window.__overlayAnimationTimeouts = [];
      }
      
      arrowHasLink = false;
      
      // Mostra cursore custom
      const customCursor = document.getElementById('magic-cursor');
      if (customCursor) customCursor.style.opacity = '1';
      
      // Animazioni uscita stagger
      const staggerDelay = 60;
      
      const leftImg = overlayLeft.querySelector('img');
      const rightImg = overlayRight.querySelector('img');
      const centerImg = overlayCenter.querySelector('img');
      const rightText = overlayRight.querySelector('.overlay-right-text');
      
      hideOverlayTimeouts.forEach(timeout => clearTimeout(timeout));
      hideOverlayTimeouts = [];

      // Center esce per primo
      hideOverlayTimeouts.push(setTimeout(() => {
        overlayCenter.style.opacity = '0';
        if (centerImg) centerImg.style.transform = 'translateY(-30px)';
      }, 0));

      // Right
      hideOverlayTimeouts.push(setTimeout(() => {
        overlayRight.style.opacity = '0';
        if (rightImg) rightImg.style.transform = 'translateX(30px)';
        if (rightText) {
          rightText.style.opacity = '0';
          rightText.style.transform = 'translateY(20px)';
        }
      }, staggerDelay));

      // Left
      hideOverlayTimeouts.push(setTimeout(() => {
        overlayLeft.style.opacity = '0';
        if (leftImg) leftImg.style.transform = 'translateX(-30px)';
      }, staggerDelay * 2));

      // Arrow e Pin - sincronizzati per transizione smooth
      hideOverlayTimeouts.push(setTimeout(() => {
        // Inizia a far scomparire la freccia
        overlayArrow.style.opacity = '0';
        
        // Rimuovi pin-hovering contemporaneamente per permettere al pin di riapparire
        // mentre la freccia sta scomparendo, creando un crossfade smooth
        if (pinToRestore) {
          requestAnimationFrame(() => {
            if (pinToRestore !== currentPin) pinToRestore.classList.remove('pin-hovering');
          });
        }
      }, staggerDelay * 3));

      // Rimuovi overlay e pulisci
      hideOverlayTimeouts.push(setTimeout(() => {
        globalOverlay.classList.remove('is-visible');
        globalOverlay.style.opacity = '0';
        globalOverlay.style.visibility = 'hidden';
        globalOverlayVisible = false;
        //console.log('👁️ Overlay nascosto');
        
        setTimeout(() => {
          if (!globalOverlay.classList.contains('is-visible')) {
            overlayArrow.innerHTML = '';
            overlayLeft.innerHTML = '';
            overlayRight.innerHTML = '';
            overlayCenter.innerHTML = '';
          }
        }, 50);
      }, 300));
    };
    // true se il puntatore è ancora sul pin attivo o su un elemento dell'overlay aperto
    const isPointerOverHoverZone = () => {
      if (!currentPin || !globalOverlay.classList.contains('is-visible')) return false;
      return [currentPin, ...clickableOverlayElements].some(isPointerInside);
    };

    const overlayClickHandler = (event) => {
      if (!currentPinData || !currentPinData.link) return;
      if (event.target.closest('a')) return; // Onora eventuali link interni
      event.preventDefault();
      navigateToLink(currentPinData.link, currentPinData);
    };

    clickableOverlayElements.forEach((el) => {
      el.classList.add('overlay-clickable');
      el.addEventListener('click', overlayClickHandler);
    });

    // Event listeners sull'overlay per mantenere visibile
    clickableOverlayElements.forEach(el => {
      el.addEventListener('mouseenter', () => {
        if (blockMouseEvents) return;
        if (Date.now() - lastGlobalTouchTime < 1000) return;
        if (currentPin) currentPin.classList.add('pin-hovering');
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          hideTimeout = null;
        }
        
        if (currentPin) {
          setFocusedPin(currentPin);
        }
      });
      
      el.addEventListener('mouseleave', () => {
        if (blockMouseEvents) return;
        if (Date.now() - lastGlobalTouchTime < 1000) return;
        
        // IMPORTANTE: Verifica che l'overlay sia effettivamente visibile prima di nasconderlo
        if (!globalOverlay.classList.contains('is-visible')) {
          return;
        }
        
        hideTimeout = setTimeout(() => {
          // Doppio controllo prima di nascondere
          if (!globalOverlay.classList.contains('is-visible')) {
            return;
          }
          // mouseleave "finto" (Chrome, cambio layout a mouse fermo): il puntatore è ancora in zona
          if (isPointerOverHoverZone()) {
            hideTimeout = null;
            return;
          }

          hideOverlay();
          currentPin = null;
          currentHoverData = null;
          currentPinIndex = null;
          currentPinData = null;
          hideTimeout = null;

          scrollVideoInstance.stopRewind();
          scrollVideoInstance.state.isHovering = false;
          
          // Controlla la posizione dello scroll corrente
          const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
          
          if (currentScrollY >= scrollVideoInstance.config.scrollTrigger) {
            // Scroll oltre la soglia -> torna alla fine
            scrollVideoInstance.playToEnd();
            resetPinsFocus();
          } else {
            // Scroll sotto la soglia -> resetta video e nascondi pin
            scrollVideoInstance.resetVideo();
            resetPinsFocus();
          }
        }, 300); // Ridotto da 600ms a 300ms per maggiore reattività
        
      });
    });
    
    // Touch sull'overlay - supporta doppio tap
    globalOverlay.addEventListener('touchstart', (e) => {
      lastGlobalTouchTime = Date.now();
      blockMouseEvents = true;
      
      if (blockMouseTimeout) clearTimeout(blockMouseTimeout);
      blockMouseTimeout = setTimeout(() => {
        blockMouseEvents = false;
      }, 2000);
      
      e.preventDefault();
      e.stopPropagation();
      
      console.log('👆 OVERLAY TOUCH');
      
      // Controlla se il tap è sul logo o sulla freccia
      const centerElement = e.target.closest('.overlay-center');
      const leftElement = e.target.closest('.overlay-left');
      const rightElement = e.target.closest('.overlay-right');
      const arrowElement = e.target.closest('.overlay-arrow');
      const arrowLink = arrowElement ? arrowElement.querySelector('.arrow-link') : null;
      
      // Se tap sul logo o freccia con link -> naviga (secondo tap)
      if ((centerElement || leftElement || rightElement || (arrowLink && arrowHasLink)) && currentPinData && currentPinData.link) {
        console.log('👆 OVERLAY TOUCH - Naviga alla cantina');
        navigateToLink(currentPinData.link, currentPinData);
      } else {
        // Altrimenti chiudi overlay e riproduci video alla fine
        console.log('👆 OVERLAY TOUCH - Chiudo overlay e riproduco video alla fine');
        if (hideTimeout) clearTimeout(hideTimeout);
        hideTimeout = null;
        arrowHasLink = false;
        
        // Stop rewind e riproduci video alla fine
        scrollVideoInstance.stopRewind();
        scrollVideoInstance.state.isHovering = false;
        scrollVideoInstance.playToEnd();
        
        hideOverlay();
        
        // Reset COMPLETO dello stato touch di tutti i pin
        pins.forEach(p => {
          if (p._touchState) {
            p._touchState.active = false;
            if (p._touchState.timeout) {
              clearTimeout(p._touchState.timeout);
              p._touchState.timeout = null;
            }
          }
          if (p._touchActive) {
            p._touchActive = false;
          }
        });
        
        resetPinsFocus();
      }
    }, { passive: false });
    
    // Resize: aggiorna posizioni overlay
    window.addEventListener('resize', () => {
      if (globalOverlay.classList.contains('is-visible') && currentPin && currentHoverData && currentPinIndex !== null) {
        const pinLink = currentPinData ? currentPinData.link : null;
        showOverlay(currentPin, currentHoverData, currentPinIndex, pinLink);
        setFocusedPin(currentPin);
      } else {
        resetPinsFocus();
      }
    });
    
    // Registra i pin con overlay
    pins.forEach((pin, index) => {
      // Calcola il target del rewind: invece di 0, usa il frame specificato
      // rewindToFrame = 1 significa che il video si ferma al frame 1 (non al frame 0)
      const rewindTarget = (scrollVideoInstance.config.rewindToFrame || 0) / scrollVideoInstance.config.fps;
      
      // Ottieni i dati del pin
      const pinData = pin._pinData || {};
      
      // console.log(`📍 Registering pin ${pin.id} with rewind target:`, rewindTarget, {
      //   hasHover: !!pinData.hover,
      //   hoverData: pinData.hover ? {
      //     logo_cantina: pinData.hover.logo_cantina || 'NON PRESENTE',
      //     img_italia: pinData.hover.img_italia || 'NON PRESENTE',
      //     img_regione: pinData.hover.img_regione || 'NON PRESENTE',
      //     regione_txt: pinData.hover.regione_txt || 'NON PRESENTE',
      //     categorie: pinData.hover.categorie || 'NON PRESENTE'
      //   } : 'NESSUN DATO HOVER'
      // });
      
      // Salva i dati del pin per il rewind
      const videoPinData = {
        element: pin,
        rewindTarget: Math.max(0, Math.min(scrollVideoInstance.config.videoDuration, rewindTarget)),
        isActive: false
      };
      
      // Timeout per l'hover enter (debounce)
      let enterTimeout = null;
      
      // Aggiungi eventi hover unificati per overlay + rewind
      pin.addEventListener('mouseenter', () => {
        if (blockMouseEvents) return;
        if (Date.now() - lastGlobalTouchTime < 1000) return;
        
        //console.log('🎯 PIN HOVER START:', pin.id);

        // Overlay già aperto su questo pin (rientro dopo un mouseleave "finto" di Chrome):
        // annulla solo la chiusura, senza rilanciare overlay e rewind
        if (currentPin === pin && globalOverlay.classList.contains('is-visible')) {
          if (hideTimeout) {
            clearTimeout(hideTimeout);
            hideTimeout = null;
          }
          return;
        }

        // Debounce dell'entrata per evitare hover accidentali
        if (enterTimeout) clearTimeout(enterTimeout);
        
        enterTimeout = setTimeout(() => {
          // Cancella timeout precedenti
          if (hideTimeout) {
            clearTimeout(hideTimeout);
            hideTimeout = null;
          }
          
          // Nascondi gli altri pin mentre questo è in focus
          setFocusedPin(pin);
          
          // Rimuovi hover dal pin precedente
          if (currentPin && currentPin !== pin) {
            currentPin.classList.remove('pin-hovering');
          }
          
          currentPin = pin;
          currentHoverData = pinData.hover;
          currentPinIndex = index;
          currentPinData = pinData;
          
          // Aggiungi classe pin-hovering (nasconde il SVG via CSS)
          pin.classList.add('pin-hovering');
          
          // Mostra overlay se ci sono dati
          if (pinData.hover) {
            showOverlay(pin, pinData.hover, index, pinData.link);
          }
          
          // ===== ATTIVA REWIND VIDEO =====
          if (!scrollVideoInstance.state.isHovering) {
            scrollVideoInstance.state.isHovering = true;
            videoPinData.isActive = true;
            
            //console.log('🎯 PIN HOVER ACTIVATED - Rewind to:', videoPinData.rewindTarget, 'Current time:', scrollVideoInstance.video.currentTime);
            
            // Ferma qualsiasi animazione in corso
            if (scrollVideoInstance.state.animationFrame) {
              cancelAnimationFrame(scrollVideoInstance.state.animationFrame);
            }
            
            // Inizia rewind fluido verso il target
            scrollVideoInstance.startRewind(videoPinData.rewindTarget);
          }
        }, 50); // 50ms di debounce per evitare hover accidentali
      });
      
      pin.addEventListener('mouseleave', () => {
        if (blockMouseEvents) return;
        if (Date.now() - lastGlobalTouchTime < 1000) return;
        
        //console.log('🎯 PIN HOVER END:', pin.id);
        
        // Cancella l'enter timeout se usciamo prima che scatti
        if (enterTimeout) {
          clearTimeout(enterTimeout);
          enterTimeout = null;
        }
        
        // Timeout più lungo per dare tempo di muoversi sull'overlay
        hideTimeout = setTimeout(() => {
          // mouseleave "finto" (Chrome, la freccia overlay compare sopra il pin a mouse fermo):
          // il puntatore è ancora sul pin o sull'overlay, non chiudere
          if (isPointerOverHoverZone()) {
            hideTimeout = null;
            return;
          }

          // ===== DISATTIVA REWIND VIDEO =====
          // Controlla lo stato globale e non solo videoPinData.isActive: il rewind può essere stato
          // avviato da un altro pin, e l'overlay può essere già nascosto. In entrambi i casi
          // isHovering resterebbe true e handleScroll ignorerebbe lo scroll per sempre.
          videoPinData.isActive = false;
          if (scrollVideoInstance.state.isHovering) {
            // Stop rewind
            scrollVideoInstance.stopRewind();
            
            // IMPORTANTE: setta isHovering a false PRIMA di chiamare playToEnd() o resetVideo()
            scrollVideoInstance.state.isHovering = false;
            
            // Controlla la posizione dello scroll corrente
            const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
            
            if (currentScrollY >= scrollVideoInstance.config.scrollTrigger) {
              // Scroll oltre la soglia -> torna alla fine
              //console.log('🎯 PIN LEAVE ACTIVATED - Return to end (scroll:', currentScrollY, ')');
              scrollVideoInstance.playToEnd();
            } else {
              // Scroll sotto la soglia -> resetta video e nascondi pin
              //console.log('🎯 PIN LEAVE ACTIVATED - Reset video (scroll:', currentScrollY, ')');
              scrollVideoInstance.resetVideo();
            }
          }
          
          // Nascondi overlay solo se è veramente visibile
          if (globalOverlay.classList.contains('is-visible')) {
            hideOverlay();
          } else if (currentPin) {
            // Overlay già chiuso: hideOverlay non ripristinerebbe l'SVG del pin
            currentPin.classList.remove('pin-hovering');
          }
          currentPin = null;
          currentHoverData = null;
          currentPinIndex = null;
          currentPinData = null;
          hideTimeout = null;

          resetPinsFocus();
        }, 150); // Ridotto da 600ms a 150ms per maggiore reattività
      });
      
      // ===== GESTIONE TOUCH PER MOBILE (DOPPIO TAP) =====
      // Salva lo stato touch sul pin stesso per poterlo resettare da qualsiasi punto
      pin._touchState = {
        active: false,
        timeout: null
      };
      
      pin.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        lastGlobalTouchTime = Date.now();
        blockMouseEvents = true;
        
        if (blockMouseTimeout) clearTimeout(blockMouseTimeout);
        blockMouseTimeout = setTimeout(() => {
          blockMouseEvents = false;
        }, 2000);
        
        console.log('👆 PIN TOUCH:', pin.id, 'touchActive:', pin._touchState.active);
        
        if (!pin._touchState.active) {
          // PRIMO TAP: Mostra overlay senza navigare
          console.log('👆 PRIMO TAP - Mostro overlay');
          pin._touchState.active = true;
          
          // Cancella timeout precedenti
          if (hideTimeout) {
            clearTimeout(hideTimeout);
            hideTimeout = null;
          }
          
          // Nascondi gli altri pin mentre questo è in focus
          setFocusedPin(pin);
          
          // Rimuovi hover dal pin precedente
          if (currentPin && currentPin !== pin) {
            currentPin.classList.remove('pin-hovering');
            // Se c'era un altro pin attivo, resettalo completamente
            pins.forEach(p => {
              if (p !== pin && p._touchState && p._touchState.active) {
                p._touchState.active = false;
                p._touchActive = false;
                if (p._touchState.timeout) {
                  clearTimeout(p._touchState.timeout);
                  p._touchState.timeout = null;
                }
              }
            });
          }
          
          currentPin = pin;
          currentHoverData = pinData.hover;
          currentPinIndex = index;
          currentPinData = pinData;
          pin._touchActive = true;
          
          // Aggiungi classe pin-hovering
          pin.classList.add('pin-hovering');
          
          // Mostra overlay
          if (pinData.hover) {
            showOverlay(pin, pinData.hover, index, pinData.link);
          }
          
          // Attiva rewind video
          if (!scrollVideoInstance.state.isHovering) {
            scrollVideoInstance.state.isHovering = true;
            videoPinData.isActive = true;
            
            if (scrollVideoInstance.state.animationFrame) {
              cancelAnimationFrame(scrollVideoInstance.state.animationFrame);
            }
            
            scrollVideoInstance.startRewind(videoPinData.rewindTarget);
          }
          
          // Dopo 5 secondi, resetta lo stato touch se non viene fatto il secondo tap
          pin._touchState.timeout = setTimeout(() => {
            pin._touchState.active = false;
            pin._touchActive = false;
            console.log('👆 TIMEOUT - Reset stato touch dopo 5 secondi');
          }, 5000);
          
        } else {
          // SECONDO TAP: Naviga alla cantina
          console.log('👆 SECONDO TAP - Naviga alla cantina');
          
          if (pin._touchState.timeout) {
            clearTimeout(pin._touchState.timeout);
            pin._touchState.timeout = null;
          }
          
          pin._touchState.active = false;
          pin._touchActive = false;
          
          // Naviga se c'è un link
          if (pinData.link) {
            navigateToLink(pinData.link, pinData);
          }
        }
      }, { passive: false });
      
      // Touch su altri elementi chiude l'overlay del pin corrente
      document.addEventListener('touchstart', (e) => {
        // Controlla se il touch è fuori dal pin e dall'overlay
        if (pin._touchState && pin._touchState.active && pin._touchActive && 
            !pin.contains(e.target) && 
            !globalOverlay.contains(e.target)) {
          
          console.log('👆 TOUCH OUTSIDE - Chiudo overlay, resetto stato e riproduco video alla fine');
          
          // Reset completo dello stato touch del pin
          pin._touchState.active = false;
          pin._touchActive = false;
          
          if (pin._touchState.timeout) {
            clearTimeout(pin._touchState.timeout);
            pin._touchState.timeout = null;
          }
          
          // Stop rewind video e riproduci verso la fine
          if (videoPinData.isActive) {
            videoPinData.isActive = false;
            scrollVideoInstance.stopRewind();
            scrollVideoInstance.state.isHovering = false;
            
            // Su mobile, quando tocchi fuori, il video va SEMPRE alla fine
            console.log('👆 TOUCH OUTSIDE - Video va alla fine');
            scrollVideoInstance.playToEnd();
          }
          
          // Nascondi overlay
          if (globalOverlay.classList.contains('is-visible')) {
            hideOverlay();
          }
          
          currentPin = null;
          currentHoverData = null;
          currentPinIndex = null;
          currentPinData = null;
          
          // Ripristina pin
          resetPinsFocus();
        }
      }, { passive: true });
    });
    //console.log(`📍 SimpleScrollVideo: Registered ${pins.length} pins with hover events and overlay`);
  }
  export default SimpleScrollVideo;